// Slice 2C2A · Shared document-default family card. (Slice 2C2B adds
// per-family localStorage draft persistence, an “Unsaved changes” pill,
// a Discard-draft affordance, stale-server-baseline handling, a pre-save
// server-conflict check, storage-event coordination for multiple tabs,
// a localStorage-unavailable warning, and a beforeunload guard when the
// draft could not be backed up.)
//
// Renders one family (Proposal · Contract · Invoice · Customer Update ·
// Closeout & Warranty). Owns its own Save button, save state, error
// state, success toast, and payload construction. Every save request
// sends ONLY that family's keys — omitted keys are preserved server-side
// by the Slice 2C1 safe partial-update endpoints.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, explainSaveError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Save, Loader2, FileText, Trash2, AlertTriangle, WifiOff } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  buildDraftKey,
  computeFingerprint,
  isStorageAvailable,
  pickFamilyValues,
  readDraft,
  removeDraft,
  valuesEqual,
  writeDraft,
} from "@/lib/documentDefaultsDrafts";
import {
  validatePayload,
  mapServerFieldErrors,
  FAMILY_FIELD_KINDS,
  BANNER_MESSAGE as PLACEHOLDER_BANNER_MESSAGE,
} from "@/lib/placeholderValidation";
import GuidedDocumentFamilyPanel from "@/components/guided/GuidedDocumentFamilyPanel";

const FIELD_MAX = 5000;
const DRAFT_DEBOUNCE_MS = 400;

const DocumentDefaultsFamilyCard = ({
  familyKey,          // "proposal" | "contract" | "invoice" | "customer_update" | "closeout"
  title,              // human-readable card title
  purpose,            // one-line purpose statement under the title
  fields,             // [{ key, label, helper, rows, placeholder }]
  values,             // { [key]: string }  — current values (parent-owned)
  onChange,           // (key, value) => void
  endpoint,           // "/document-defaults" | "/closeout-defaults"
  saveButtonLabel,    // e.g. "Save proposal defaults"
  successMessage,     // e.g. "Proposal defaults saved. They'll apply to future proposals."
  snapshotNote,       // per-family future-doc snapshot wording
  // Slice 2C2B additions ---
  userId,             // authenticated user id (used to namespace drafts)
  baseline,           // { [key]: string } — last server-hydrated OR last-saved values for THIS family
  baselineUpdatedAt,  // ISO string from server, when available
  onBaselineReplace,  // (familyValues, updatedAt) => void — parent absorbs a new baseline (post-save / post-refetch)
  onFamilyValuesReplace, // (familyValues) => void — parent replaces THIS family's values in-place
  refetchFamily,      // async () => ({ values, updated_at })  — parent GETs endpoint and returns THIS family's slice
  onCardStateChange,  // Slice 2C3 (additive, report-only) — { dirty, saving, staleDraft, saveConflict, otherTab } → void
  // Slice 2C5A additions ---
  // guidedMode:
  //   null | undefined  — legacy raw-editor experience (Slice 2C4 baseline, byte-identical behaviour)
  //   { editorRevealed, ...panelProps }  — render GuidedDocumentFamilyPanel INSTEAD of raw editor
  //     until the contractor picks "Edit wording" and `editorRevealed` flips to true.
  guidedMode,
}) => {
  const { t } = useLang();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Slice 2C4 · Per-field placeholder / fake-contact validation errors.
  // Populated by the client pre-save check and by mapping the server's
  // atomic 422 response. Cleared when the contractor edits the value.
  const [placeholderErrors, setPlaceholderErrors] = useState({});
  const [storageOk] = useState(() => isStorageAvailable());
  // draftBackedUp becomes true once the current diff has been written to
  // localStorage. Used by beforeunload to only warn when we truly could
  // not persist a backup.
  const [draftBackedUp, setDraftBackedUp] = useState(true);

  // stale-server-baseline banner (draft was created against an older server value)
  const [staleDraft, setStaleDraft] = useState(null); // { values, baselineFingerprint }
  // pre-save server-conflict banner
  const [saveConflict, setSaveConflict] = useState(null); // { latestValues, latestUpdatedAt }
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);
  // other-tab draft-change banner
  const [otherTab, setOtherTab] = useState(null); // { incomingValues } | { cleared: true }
  const [keepTabConfirmOpen, setKeepTabConfirmOpen] = useState(false);
  // discard-draft confirmation
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);

  const fieldKeys = useMemo(() => fields.map((f) => f.key), [fields]);

  // Current family values (only THIS family's keys)
  const familyValues = useMemo(() => pickFamilyValues(values, fieldKeys), [values, fieldKeys]);
  const baselineFamily = useMemo(() => pickFamilyValues(baseline || {}, fieldKeys), [baseline, fieldKeys]);
  const baselineFingerprint = useMemo(
    () => computeFingerprint(baselineFamily, fieldKeys),
    [baselineFamily, fieldKeys],
  );

  // Dirty = current values differ from baseline (explicit "" respected).
  const dirty = useMemo(
    () => !valuesEqual(familyValues, baselineFamily, fieldKeys),
    [familyValues, baselineFamily, fieldKeys],
  );

  // Keep a ref of the current family values so the storage-event handler
  // (registered with stable deps) can always compare against the latest
  // in-memory values without being re-registered on every keystroke.
  const familyValuesRef = useRef(familyValues);
  useEffect(() => {
    familyValuesRef.current = familyValues;
  });

  // Slice 2C3 (additive, report-only) — surface visible state to the
  // wizard parent so it can gate its "Mark ready" / "Set up later"
  // status buttons on unresolved dirty / draft / conflict conditions.
  // This callback fires whenever any of these values change; it never
  // modifies card behavior or contract.
  useEffect(() => {
    if (typeof onCardStateChange === "function") {
      onCardStateChange({
        dirty: !!dirty,
        saving: !!saving,
        staleDraft: !!staleDraft,
        saveConflict: !!saveConflict,
        otherTab: !!otherTab,
        // Slice 2C4 — report placeholder validation state so Step 3
        // can gate Mark ready / Set up later on it. Uses live in-memory
        // values so the block is immediate as the contractor types.
        placeholderBlocked: Object.keys(
          validatePayload(familyValues, FAMILY_FIELD_KINDS[familyKey] || {}),
        ).length > 0,
      });
    }
  }, [dirty, saving, staleDraft, saveConflict, otherTab, familyValues, familyKey, onCardStateChange]);

  // ------------------------------------------------------------------
  // Slice 2C2B — DIRTY LATCH (contractor-data-loss protection)
  // ------------------------------------------------------------------
  // Once the family has any dirty in-memory value, this latch permanently
  // blocks every IMPLICIT value/baseline replacement until an EXPLICIT
  // contractor action resolves the dirty state. Every automatic path
  // (initial hydration, storage-event handler, focus/visibility, remount
  // hydration, background refetch application) must consult this latch
  // before touching parent state.
  //
  // The latch is CLEARED only inside sanctioned handlers:
  //   * successful save (server response absorbed as new baseline)
  //   * confirmDiscard    — contractor explicitly discarded their draft
  //   * resolveConflictByLoadingLatest — “Load latest saved values”
  //   * loadOtherTabDraft — “Load newer draft” from another tab
  //   * restoreStaleDraft — “Restore my draft” after a stale-baseline banner
  //
  // No other code path may write to dirtyLatchRef.current.
  const dirtyLatchRef = useRef(false);
  useEffect(() => {
    // Only ever LATCHES; never automatically unlatches. Unlatch is
    // performed by the sanctioned handlers below.
    if (dirty) dirtyLatchRef.current = true;
  }, [dirty]);

  const clearDirtyLatch = useCallback(() => {
    dirtyLatchRef.current = false;
  }, []);
  const isDirtyLocked = useCallback(
    () => dirtyLatchRef.current === true,
    [],
  );

  // ----- Draft restore on mount / when user identity or baseline changes -----
  // We ONLY check once per (userId, familyKey, baseline-identity) combination.
  // lastKnownEditedAtRef tracks the newest editedAt this tab has ever
  // written or ingested — used by the storage handler to ignore stale
  // or byte-identical duplicate events fired by other tabs.
  const hydratedRef = useRef(false);
  const lastKnownEditedAtRef = useRef("");
  useEffect(() => {
    if (!userId) return;
    if (hydratedRef.current) return;
    // Slice 2C2B blocker fix: if this card already has dirty values
    // (e.g. a StrictMode double-mount, remount-like effect, or any
    // background rerun of this hydration effect), NEVER overwrite the
    // contractor's in-flight edits with a localStorage draft or with
    // any server-derived value. The dirty latch is the single hard
    // stop that protects unsaved values from silent data loss.
    if (isDirtyLocked()) return;
    hydratedRef.current = true;
    const draft = readDraft(userId, familyKey);
    if (!draft) return;
    // If the draft's baseline fingerprint matches the current server
    // baseline, restore silently and mark unsaved. Otherwise show the
    // stale-draft banner and DO NOT silently overlay.
    if (draft.baselineFingerprint === baselineFingerprint) {
      onFamilyValuesReplace?.(draft.values);
      lastKnownEditedAtRef.current = draft.editedAt || "";
      // Draft was already backed up; keep flag true.
      setDraftBackedUp(true);
    } else {
      setStaleDraft(draft);
      lastKnownEditedAtRef.current = draft.editedAt || "";
    }
  }, [userId, familyKey, baselineFingerprint, onFamilyValuesReplace, isDirtyLocked]);

  // ----- Debounced localStorage write / remove on any value change -----
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!userId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!dirty) {
        // Returning to baseline removes only this family's draft.
        removeDraft(userId, familyKey);
        setDraftBackedUp(true);
        return;
      }
      // Slice 2C2B correction (defect 1): if the record already stored
      // under our exact key has semantically identical family values,
      // do NOT rewrite it merely to generate a newer editedAt. This
      // prevents a duplicate tab (which restored the same valid draft)
      // from broadcasting a spurious "newer" storage event to peers.
      const existing = readDraft(userId, familyKey);
      if (existing && valuesEqual(familyValues, existing.values, fieldKeys)) {
        lastKnownEditedAtRef.current = existing.editedAt || lastKnownEditedAtRef.current;
        setDraftBackedUp(true);
        return;
      }
      const editedAt = writeDraft(userId, familyKey, {
        values: familyValues,
        fieldKeys,
        baselineFingerprint,
        baselineUpdatedAt,
      });
      if (editedAt) {
        lastKnownEditedAtRef.current = editedAt;
        setDraftBackedUp(true);
      } else {
        setDraftBackedUp(false);
      }
    }, DRAFT_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // familyValues is derived from `values`, so this effect naturally
    // reruns on every keystroke.
  }, [userId, familyKey, dirty, familyValues, fieldKeys, baselineFingerprint, baselineUpdatedAt]);

  // ----- beforeunload guard — only when dirty AND draft not backed up -----
  useEffect(() => {
    if (!(dirty && !draftBackedUp)) return;
    const handler = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to be set.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, draftBackedUp]);

  // ----- storage event: another tab wrote or cleared THIS family's draft -----
  // Slice 2C2B correction: only surface the other-tab banner when the
  // incoming payload is GENUINELY newer than what this tab already
  // knows about. Older or byte-identical events are ignored. In-memory
  // field values are NEVER replaced from inside this handler.
  useEffect(() => {
    if (!userId) return;
    const myKey = buildDraftKey(userId, familyKey);
    const handler = (e) => {
      if (!e || e.storageArea !== window.localStorage) return;
      if (e.key !== myKey) return;
      // Ignore no-op events (same value / same key rewritten).
      if (e.oldValue === e.newValue) return;
      // Cleared / removed key: another tab (typically after a successful
      // save) deleted the shared draft. This must NEVER cause a value
      // reset in the current tab. Only surface the blue banner when
      // the current tab has unresolved unsaved values, and treat the
      // local backup as no-longer-present so beforeunload protects
      // against accidental navigation until the contractor chooses.
      //
      // Explicit invariants enforced here (Slice 2C2B blocker fix):
      //   * NEVER call onFamilyValuesReplace.
      //   * NEVER call onBaselineReplace.
      //   * NEVER call refetchFamily.
      //   * NEVER clear the dirty latch (only sanctioned handlers do).
      if (e.newValue == null) {
        if (dirty) {
          setOtherTab({ cleared: true });
          setDraftBackedUp(false);
        }
        return;
      }
      // A draft arrived from another tab. Validate + timestamp-check.
      let parsed;
      try {
        parsed = JSON.parse(e.newValue);
      } catch {
        return; // malformed events are ignored (never overlay)
      }
      if (!parsed || typeof parsed !== "object") return;
      if (parsed.schemaVersion !== 1) return;
      if (parsed.userId !== userId) return;
      if (parsed.familyKey !== familyKey) return;
      if (!parsed.values || typeof parsed.values !== "object") return;
      const incomingEditedAt = typeof parsed.editedAt === "string" ? parsed.editedAt : "";
      const known = lastKnownEditedAtRef.current || "";
      // Strict greater-than: ISO-8601 strings sort chronologically.
      // Older or duplicate timestamps → ignore.
      if (!incomingEditedAt || incomingEditedAt <= known) return;
      // Slice 2C2B correction (defect 1): a NEWER timestamp with
      // semantically identical family values must NOT raise the banner.
      // Advance the known timestamp so subsequent identical broadcasts
      // are ignored, but never require contractor action and never
      // replace field values.
      const currentValues = familyValuesRef.current;
      if (valuesEqual(parsed.values, currentValues, fieldKeys)) {
        lastKnownEditedAtRef.current = incomingEditedAt;
        return;
      }
      lastKnownEditedAtRef.current = incomingEditedAt;
      setOtherTab({ incomingValues: parsed.values });
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [userId, familyKey, dirty]);

  // ---------------------------------------------------------------- actions
  const applyDraftToValues = (draftValues) => {
    onFamilyValuesReplace?.(pickFamilyValues(draftValues, fieldKeys));
  };

  const restoreStaleDraft = () => {
    if (!staleDraft) return;
    applyDraftToValues(staleDraft.values);
    setStaleDraft(null);
    // Sanctioned handler: contractor explicitly chose to restore.
    // (Values immediately differ from baseline → dirty latches again on next tick.)
    clearDirtyLatch();
    // The draft becomes “current” against the NEW baseline. Rewrite it
    // so its stored baseline fingerprint matches, so subsequent tabs
    // don’t re-flag it as stale.
    if (userId) {
      const editedAt = writeDraft(userId, familyKey, {
        values: pickFamilyValues(staleDraft.values, fieldKeys),
        fieldKeys,
        baselineFingerprint,
        baselineUpdatedAt,
      });
      if (editedAt) lastKnownEditedAtRef.current = editedAt;
    }
  };

  const discardStaleDraft = () => {
    if (!userId) return;
    removeDraft(userId, familyKey);
    setStaleDraft(null);
  };

  const openDiscardDialog = () => {
    if (!dirty) return;
    setDiscardOpen(true);
  };

  const confirmDiscard = async () => {
    if (!userId) return;
    setDiscardBusy(true);
    try {
      // Re-fetch so “latest saved values” is accurate at discard time.
      let latest = null;
      try {
        latest = await refetchFamily?.();
      } catch {
        /* fall through with existing baseline */
      }
      const latestValues = latest?.values || baselineFamily;
      const latestUpdatedAt = latest?.updated_at || baselineUpdatedAt || "";
      onFamilyValuesReplace?.(pickFamilyValues(latestValues, fieldKeys));
      onBaselineReplace?.(pickFamilyValues(latestValues, fieldKeys), latestUpdatedAt);
      removeDraft(userId, familyKey);
      setDraftBackedUp(true);
      // Sanctioned handler: contractor explicitly discarded their draft.
      clearDirtyLatch();
      setOtherTab(null);
      setStaleDraft(null);
      setDiscardOpen(false);
    } finally {
      setDiscardBusy(false);
    }
  };

  // Overwrite path uses a ref so the save() signature stays `async ()`.
  // The ref is consumed on entry and reset immediately, so a plain save
  // click never inherits a stale overwrite intent.
  const overwritingRef = useRef(false);
  const save = async () => {
    if (saving) return;
    const overwriting = overwritingRef.current;
    overwritingRef.current = false;
    setSaving(true);
    setError("");
    // Build a payload containing ONLY this family's keys.
    // Empty strings must survive as explicit clears — do NOT filter them out.
    const payload = {};
    for (const f of fields) {
      payload[f.key] = values[f.key] ?? "";
    }
    // Slice 2C4 · Client-side pre-save validation. Blocks the API call
    // when the current values contain unsafe placeholder / example /
    // fake-contact data. Draft + Unsaved-changes state is preserved —
    // we never clear localStorage on failed validation.
    const clientErrors = validatePayload(payload, FAMILY_FIELD_KINDS[familyKey] || {});
    if (Object.keys(clientErrors).length > 0) {
      setPlaceholderErrors(clientErrors);
      setSaving(false);
      return;
    }
    try {
      if (!overwriting) {
        // Pre-save server-conflict check.
        const latest = await refetchFamily?.();
        if (latest) {
          const latestFingerprint = computeFingerprint(latest.values || {}, fieldKeys);
          if (latestFingerprint !== baselineFingerprint) {
            setSaveConflict({
              latestValues: pickFamilyValues(latest.values || {}, fieldKeys),
              latestUpdatedAt: latest.updated_at || "",
            });
            setSaving(false);
            return;
          }
        }
      }
      // Slice 2C5A · Guided-mode customized save routes through the
      // strict-ordered guided endpoint (PUT /document-guided-setup/
      // proposal with action=save_customized). The handler returns a
      // `{ data }` in the same shape as api.put so the absorb-response
      // block below stays byte-identical for the raw-editor branch.
      // We intentionally set the latch ref directly (mirroring the
      // sanctioned unlatch semantics) instead of adding a new
      // helper-invocation site — Slice 2C2B lock preserved.
      if (guidedMode && typeof guidedMode.saveCustomizedHandler === "function") {
        const gres = await guidedMode.saveCustomizedHandler(payload);
        const gFamily = pickFamilyValues(gres?.data || {}, fieldKeys);
        const gUpdatedAt = gres?.data?.updated_at || "";
        onBaselineReplace?.(gFamily, gUpdatedAt);
        if (userId) removeDraft(userId, familyKey);
        setDraftBackedUp(true);
        dirtyLatchRef.current = false;
        setPlaceholderErrors({});
        setSaveConflict(null);
        setOverwriteConfirmOpen(false);
        setOtherTab(null);
        setStaleDraft(null);
        toast.success(t(successMessage));
        return;
      }
      const res = await api.put(endpoint, payload);
      // Absorb response as new baseline. Response body reflects the
      // full document; we only care about this family's fields.
      const respFamily = pickFamilyValues(res?.data || {}, fieldKeys);
      const respUpdatedAt = res?.data?.updated_at || "";
      onBaselineReplace?.(respFamily, respUpdatedAt);
      // Only clear THIS family's draft. Other families unaffected.
      if (userId) removeDraft(userId, familyKey);
      setDraftBackedUp(true);
      // Sanctioned handler: successful save resolved the dirty state.
      clearDirtyLatch();
      // Slice 2C4 · Successful save clears the placeholder-error state.
      setPlaceholderErrors({});
      // Slice 2C2B correction (defect 3): confirmation-resolution
      // cleanup — any blue other-tab or stale-draft state that was
      // hanging around is now obsolete for this family. Other families
      // are untouched.
      setSaveConflict(null);
      setOverwriteConfirmOpen(false);
      setOtherTab(null);
      setStaleDraft(null);
      toast.success(t(successMessage));
    } catch (err) {
      // Slice 2C4 · Map atomic placeholder-rejection 422 into the same
      // per-field UI. Draft, dirty state, and localStorage remain
      // untouched — the contractor must correct the field.
      const serverPlaceholderErrors = mapServerFieldErrors(err);
      if (Object.keys(serverPlaceholderErrors).length > 0) {
        setPlaceholderErrors(serverPlaceholderErrors);
        // Do not emit a full error banner — the field-level messages
        // + the summary banner already tell the story.
      } else {
        const msg = explainSaveError(err, t(title).toLowerCase());
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmOverwriteAndSave = () => {
    overwritingRef.current = true;
    save();
  };

  const resolveConflictByLoadingLatest = () => {
    if (!saveConflict) return;
    onFamilyValuesReplace?.(pickFamilyValues(saveConflict.latestValues, fieldKeys));
    onBaselineReplace?.(pickFamilyValues(saveConflict.latestValues, fieldKeys), saveConflict.latestUpdatedAt);
    if (userId) removeDraft(userId, familyKey);
    setDraftBackedUp(true);
    // Sanctioned handler: contractor chose to accept the newer server value.
    clearDirtyLatch();
    // Slice 2C2B correction (defect 3): loading the latest saved values
    // resolves the conflict without a PUT and without a save toast; it
    // also invalidates any pending blue other-tab or stale-draft state
    // for THIS family. Other families remain untouched.
    setSaveConflict(null);
    setOtherTab(null);
    setStaleDraft(null);
  };

  const loadOtherTabDraft = () => {
    if (!otherTab || otherTab.cleared) {
      // Cleared → also just refetch so we're synced.
      setOtherTab(null);
      return;
    }
    applyDraftToValues(otherTab.incomingValues);
    // Sanctioned handler: contractor explicitly accepted the other tab's draft.
    clearDirtyLatch();
    setOtherTab(null);
  };

  const keepThisTabsChanges = () => {
    // Explicit confirmation before rewriting our own draft on top of
    // the other tab's version.
    setKeepTabConfirmOpen(true);
  };

  const confirmKeepThisTabsChanges = () => {
    if (!userId) return;
    const editedAt = writeDraft(userId, familyKey, {
      values: familyValues,
      fieldKeys,
      baselineFingerprint,
      baselineUpdatedAt,
    });
    if (editedAt) {
      lastKnownEditedAtRef.current = editedAt;
      setDraftBackedUp(true);
    } else {
      setDraftBackedUp(false);
    }
    setOtherTab(null);
    setKeepTabConfirmOpen(false);
  };

  // ---------------------------------------------------------------- render

  const onFieldChange = useCallback(
    (key, value) => {
      if (value.length > FIELD_MAX) return;
      onChange(key, value);
      // Mark unbacked-up immediately; the debounced effect will flip it
      // to true after the write succeeds. If storage is unavailable, the
      // debounced write returns false and we stay unbacked-up (which
      // arms the beforeunload guard).
      setDraftBackedUp(false);
      // Slice 2C4 · Clear the field-level placeholder error the moment
      // the contractor starts editing it. Server validation remains
      // authoritative on the next Save attempt.
      setPlaceholderErrors((prev) => {
        if (!prev || !prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [onChange],
  );

  return (
    <section
      className="jc-card p-6"
      data-testid={`family-card-${familyKey}`}
      data-family-key={familyKey}
    >
      <div className="flex items-center gap-2 mb-1">
        <FileText size={14} className="text-[var(--jc-text-soft)]" />
        <h3 className="font-display text-lg" data-testid={`family-card-${familyKey}-title`}>
          {t(title)}
        </h3>
        {dirty && (
          <span
            className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
            data-testid={`family-card-${familyKey}-unsaved-pill`}
          >
            {t("family_card.unsaved_pill")}
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--jc-text-soft)] mb-2">{t(purpose)}</p>
      <p
        className="text-[11px] text-[var(--jc-text-soft)] italic mb-4 leading-relaxed"
        data-testid={`family-card-${familyKey}-snapshot-note`}
      >
        {t(snapshotNote)}
      </p>

      {!storageOk && (
        <div
          className="mb-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid={`family-card-${familyKey}-storage-warning`}
        >
          <WifiOff size={14} className="mt-0.5 shrink-0" />
          <span>
            {t("family_card.storage_warning")}
          </span>
        </div>
      )}

      {staleDraft && (
        <div
          className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid={`family-card-${familyKey}-stale-banner`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{t("family_card.stale_banner")}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={restoreStaleDraft}
              data-testid={`family-card-${familyKey}-stale-restore`}
              className="px-2 py-1 text-xs font-semibold rounded border border-amber-400 bg-white hover:bg-amber-100"
            >
              {t("family_card.stale_restore")}
            </button>
            <button
              type="button"
              onClick={discardStaleDraft}
              data-testid={`family-card-${familyKey}-stale-discard`}
              className="px-2 py-1 text-xs font-semibold rounded border border-amber-400 bg-white hover:bg-amber-100"
            >
              {t("family_card.stale_discard")}
            </button>
          </div>
        </div>
      )}

      {saveConflict && (
        <div
          className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
          data-testid={`family-card-${familyKey}-conflict-banner`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{t("family_card.conflict_banner")}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={resolveConflictByLoadingLatest}
              data-testid={`family-card-${familyKey}-conflict-load-latest`}
              className="px-2 py-1 text-xs font-semibold rounded border border-red-400 bg-white hover:bg-red-100"
            >
              {t("family_card.load_latest")}
            </button>
            <button
              type="button"
              onClick={() => setOverwriteConfirmOpen(true)}
              data-testid={`family-card-${familyKey}-conflict-overwrite`}
              className="px-2 py-1 text-xs font-semibold rounded border border-red-400 bg-white hover:bg-red-100"
            >
              {t("family_card.overwrite")}
            </button>
          </div>
        </div>
      )}

      {/* Slice 2C2B correction (defect 2): while the red server-conflict
          banner is active, suppress the blue other-tab banner so the
          contractor sees exactly one clear decision (Load latest /
          Overwrite). Underlying otherTab state is preserved and will
          be cleared explicitly by conflict resolution below. */}
      {otherTab && !saveConflict && (
        <div
          className="mb-4 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
          data-testid={`family-card-${familyKey}-other-tab-banner`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{t("family_card.other_tab_banner")}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={loadOtherTabDraft}
              data-testid={`family-card-${familyKey}-other-tab-load`}
              className="px-2 py-1 text-xs font-semibold rounded border border-sky-400 bg-white hover:bg-sky-100"
            >
              {t("family_card.load_newer")}
            </button>
            <button
              type="button"
              onClick={keepThisTabsChanges}
              data-testid={`family-card-${familyKey}-other-tab-keep`}
              className="px-2 py-1 text-xs font-semibold rounded border border-sky-400 bg-white hover:bg-sky-100"
            >
              {t("family_card.keep_this_tab")}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Slice 2C5A · Guided intake for supported families. When
            guidedMode is present AND the contractor has not switched
            to the raw editor, render the guided panel instead of the
            paragraph fields. All other card protections (Unsaved pill,
            conflict banners, discard dialog) still render above. */}
        {guidedMode && !guidedMode.editorRevealed ? (
          <GuidedDocumentFamilyPanel
            family={familyKey}
            userId={guidedMode.userId || userId}
            companyProfile={guidedMode.companyProfile}
            guidedRecord={guidedMode.guidedRecord}
            savedWording={values}
            onSaveGuided={guidedMode.onSaveGuided}
            onSetLater={guidedMode.onSetLater}
            onSwitchToEditor={guidedMode.onSwitchToEditor}
            onNavigateToBusinessAndTrades={guidedMode.onNavigateToBusinessAndTrades}
          />
        ) : (
        <>
        {Object.keys(placeholderErrors).length > 0 && (
          <div
            className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
            data-testid={`family-card-${familyKey}-placeholder-banner`}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{PLACEHOLDER_BANNER_MESSAGE}</span>
            </div>
          </div>
        )}
        {fields.map((f) => (
          <label key={f.key} className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t(f.label)}</span>
            {f.helper && (
              <span className="text-[11px] text-[var(--jc-text-soft)] leading-relaxed">
                {t(f.helper)}
              </span>
            )}
            <textarea
              data-testid={`family-card-${familyKey}-field-${f.key}`}
              rows={f.rows || 3}
              className="jc-input font-mono text-sm"
              placeholder={f.placeholder ? t(f.placeholder) : ""}
              value={values[f.key] || ""}
              onChange={(e) => onFieldChange(f.key, e.target.value)}
            />
            {placeholderErrors[f.key] && (
              <span
                className="text-[11px] text-red-700 leading-relaxed"
                data-testid={`family-card-${familyKey}-field-${f.key}-placeholder-error`}
              >
                {placeholderErrors[f.key]}
              </span>
            )}
            <span
              className="text-[10px] text-[var(--jc-text-soft)] self-end"
              data-testid={`family-card-${familyKey}-counter-${f.key}`}
            >
              {(values[f.key] || "").length} / {FIELD_MAX}
            </span>
          </label>
        ))}
        </>
        )}
      </div>

      {error && (
        <div
          className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          data-testid={`family-card-${familyKey}-error`}
        >
          {error}
        </div>
      )}

      {!(guidedMode && !guidedMode.editorRevealed) && (
      <div className="mt-4 flex items-center justify-end gap-2">
        {dirty && (
          <button
            type="button"
            onClick={openDiscardDialog}
            disabled={saving}
            data-testid={`family-card-${familyKey}-discard`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            <Trash2 size={14} />
            {t("family_card.discard_draft")}
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`family-card-${familyKey}-save`}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
          {saving ? t("family_card.saving") : t(saveButtonLabel)}
        </button>
      </div>
      )}

      {/* Discard-draft confirmation */}
      <AlertDialog open={discardOpen} onOpenChange={(o) => !discardBusy && setDiscardOpen(o)}>
        <AlertDialogContent data-testid={`family-card-${familyKey}-discard-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("family_card.discard_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("family_card.discard_dialog_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={discardBusy}
              data-testid={`family-card-${familyKey}-discard-cancel`}
            >
              {t("family_card.keep_editing")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={discardBusy}
              onClick={confirmDiscard}
              data-testid={`family-card-${familyKey}-discard-confirm`}
            >
              {t("family_card.discard_changes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Overwrite-with-my-draft confirmation */}
      <AlertDialog open={overwriteConfirmOpen} onOpenChange={setOverwriteConfirmOpen}>
        <AlertDialogContent data-testid={`family-card-${familyKey}-overwrite-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("family_card.overwrite_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("family_card.overwrite_dialog_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`family-card-${familyKey}-overwrite-cancel`}
            >
              {t("family_card.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmOverwriteAndSave}
              data-testid={`family-card-${familyKey}-overwrite-confirm`}
            >
              {t("family_card.overwrite")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Keep-this-tab's-changes confirmation */}
      <AlertDialog open={keepTabConfirmOpen} onOpenChange={setKeepTabConfirmOpen}>
        <AlertDialogContent data-testid={`family-card-${familyKey}-keep-tab-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("family_card.keep_tab_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("family_card.keep_tab_dialog_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`family-card-${familyKey}-keep-tab-cancel`}
            >
              {t("family_card.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmKeepThisTabsChanges}
              data-testid={`family-card-${familyKey}-keep-tab-confirm`}
            >
              {t("family_card.keep_this_tab")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default DocumentDefaultsFamilyCard;
export { FIELD_MAX, DRAFT_DEBOUNCE_MS };
